import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { MaybeRefOrGetter, Ref } from 'vue'
import { toValue } from 'vue'
import { useEventListener } from '@vueuse/core'
import debounce from 'lodash/debounce'

import { isUsingNewLayout } from '@app/adapters/silverfin'
import { isScrolledIntoView } from '@app/utils/domHelpers'
import { LeftDrawerCacheService } from '@app/shared/services/left-drawer-cache'

import { useOnScrollable } from './use-on-scrollable'

const SCROLL_SAVE_DEBOUNCE_MS = 50

const getSavedScrollPosition = (workflowId: number): number | null => {
  const cacheKey = LeftDrawerCacheService.buildCacheKey({
    currentFirmId: window.Silverfin.currentFirmId,
    currentCompanyId: window.Silverfin.currentCompanyId,
    ledgerId: window.Silverfin.currentLedgerId(),
    workflowId,
  })

  return LeftDrawerCacheService.getScrollPosition(cacheKey)
}

/**
 * Persists and restores the navigation drawer scroll position for a given workflow.
 * Saves scroll position to `LeftDrawerCacheService` (keyed by ledger + workflow ID) and
 * restores it once the scrollable container is ready.
 */
export function useDrawerScrollPosition(
  scrollableContainer: Ref<HTMLElement | null>,
  loading: Ref<boolean>,
  workflowId: MaybeRefOrGetter<number>
): {
  isScrollable: Ref<boolean>
  scrollIntoViewTimestamp: Ref<number>
  hasSetActiveItemOnce: Ref<boolean>
  handleScrollOffsetElement: (targetElement: HTMLElement) => Promise<void>
} {
  const registeredScrollContainer = ref<HTMLElement | null>(null)
  const scrollToElement = ref<HTMLElement | null>(null)
  const scrollIntoViewTimestamp = ref(0)
  const hasSetActiveItemOnce = ref(false)
  let hasAppliedScrollPosition = false
  let applyScrollPositionTimeout: ReturnType<typeof setTimeout> | null = null

  const usingNewLayout = isUsingNewLayout()

  function applyScrollPosition(): void {
    if (hasAppliedScrollPosition) return

    const saved = getSavedScrollPosition(toValue(workflowId))
    if (saved !== null && registeredScrollContainer.value) {
      hasAppliedScrollPosition = true
      registeredScrollContainer.value.scrollTo({ top: Number(saved) })
    }
  }

  function saveScrollPosition(): void {
    if (registeredScrollContainer.value) {
      const cacheKey = LeftDrawerCacheService.buildCacheKey({
        currentFirmId: window.Silverfin.currentFirmId,
        currentCompanyId: window.Silverfin.currentCompanyId,
        ledgerId: window.Silverfin.currentLedgerId(),
        workflowId: toValue(workflowId),
      })

      LeftDrawerCacheService.setScrollPosition(
        cacheKey,
        registeredScrollContainer.value.scrollTop
      )
    }
  }
  const debouncedSaveScrollPosition = debounce(
    saveScrollPosition,
    SCROLL_SAVE_DEBOUNCE_MS
  )

  useEventListener(
    registeredScrollContainer,
    'scroll',
    debouncedSaveScrollPosition
  )

  const isScrollable = useOnScrollable(
    scrollableContainer,
    loading,
    getSavedScrollPosition(toValue(workflowId))
  )

  watch(isScrollable, () => {
    registeredScrollContainer.value = scrollableContainer.value

    if (applyScrollPositionTimeout !== null) {
      clearTimeout(applyScrollPositionTimeout)
    }

    applyScrollPositionTimeout = setTimeout(() => applyScrollPosition(), 0)
  })

  function hasSavedScrollPosition(): boolean {
    return getSavedScrollPosition(toValue(workflowId)) !== null
  }

  function checkIsScrolledIntoView(el: HTMLElement): boolean {
    return (
      !!registeredScrollContainer.value &&
      isScrolledIntoView(el, registeredScrollContainer.value)
    )
  }

  function scrollToElementWithOffset(targetElement: HTMLElement): void {
    if (usingNewLayout && hasSavedScrollPosition()) {
      applyScrollPosition()

      window.requestAnimationFrame(() => {
        if (checkIsScrolledIntoView(targetElement)) {
          scrollIntoViewTimestamp.value = 0
          hasSetActiveItemOnce.value = true
          return
        }

        if (scrollableContainer.value) {
          const spaceBetween =
            targetElement.getBoundingClientRect().top -
            scrollableContainer.value.getBoundingClientRect().top +
            scrollableContainer.value.scrollTop

          scrollableContainer.value.scrollTo({
            top: spaceBetween - 80,
          })

          scrollIntoViewTimestamp.value = 0
          hasSetActiveItemOnce.value = true
        }
      })
      return
    }

    if (scrollableContainer.value) {
      const spaceBetween =
        targetElement.getBoundingClientRect().top -
        scrollableContainer.value.getBoundingClientRect().top +
        scrollableContainer.value.scrollTop

      scrollableContainer.value.scrollTo({
        top: spaceBetween - 80,
      })

      scrollIntoViewTimestamp.value = 0
      hasSetActiveItemOnce.value = true
    }
  }

  async function handleScrollOffsetElement(
    targetElement: HTMLElement
  ): Promise<void> {
    await nextTick()
    await nextTick()

    if (!isScrollable.value) {
      scrollToElement.value = targetElement
    } else {
      scrollToElementWithOffset(targetElement)
    }
  }

  watch(
    [isScrollable, scrollToElement],
    ([isScrollableValue, scrollToElementValue]) => {
      if (isScrollableValue && scrollToElementValue) {
        scrollToElementWithOffset(scrollToElementValue)
        scrollToElement.value = null
      }
    }
  )

  onBeforeUnmount(() => {
    if (applyScrollPositionTimeout !== null) {
      clearTimeout(applyScrollPositionTimeout)
      applyScrollPositionTimeout = null
    }
    debouncedSaveScrollPosition.cancel()
  })

  return {
    isScrollable,
    scrollIntoViewTimestamp,
    hasSetActiveItemOnce,
    handleScrollOffsetElement,
  }
}
